import { useState, type FormEvent, type ReactElement } from 'react';

import { solveAltchaChallenge } from './native-altcha-client';
import type { NativeBookingApi, NativeMe } from './native-booking-api';
import { nativeProblemMessage } from './native-booking-problem';
import { navigate } from './path-router';

export function NativeBookingAccount(props: {
  readonly api: NativeBookingApi;
  readonly me: NativeMe | null;
  readonly onSession: (me: NativeMe | null) => void;
}): ReactElement {
  const [email, setEmail] = useState(props.me?.email ?? '');
  const [code, setCode] = useState('');
  const [phase, setPhase] = useState<'email' | 'code'>('email');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const requestCode = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const challenge = await props.api.fetchAltchaChallenge();
      const altcha = await solveAltchaChallenge(challenge);
      await props.api.requestOtp(email.trim(), altcha);
      setPhase('code');
      setInfo('Код отправлен на указанный адрес электронной почты.');
    } catch (failure) {
      setError(nativeProblemMessage(failure));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await props.api.verifyOtp(email.trim(), code.trim());
      const me = await props.api.me();
      props.onSession(me);
      setInfo('Вход выполнен.');
      navigate('/booking', 'replace');
    } catch (failure) {
      setError(nativeProblemMessage(failure));
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    setBusy(true);
    setError(null);
    try {
      await props.api.logout();
      props.onSession(null);
      setPhase('email');
      setCode('');
      setInfo('Вы вышли из личного кабинета.');
    } catch (failure) {
      setError(nativeProblemMessage(failure));
    } finally {
      setBusy(false);
    }
  };

  if (props.me !== null && phase === 'email') {
    return (
      <div className="native-auth-center">
        <section className="native-auth-card" aria-labelledby="native-account-title">
          <div className="native-auth-card-top">
            <h1 id="native-account-title">Пользователь</h1>
            <p>{props.me.email}</p>
          </div>
          <div className="native-auth-content">
            {info !== null ? (
              <p role="status" className="native-banner native-banner-success">
                {info}
              </p>
            ) : null}
            {error !== null ? (
              <p role="alert" className="native-banner native-banner-danger">
                {error}
              </p>
            ) : null}
            <div className="native-auth-actions">
              <button type="button" className="native-btn native-btn-primary" onClick={() => navigate('/booking')}>
                Новая запись
              </button>
              <button type="button" className="native-btn native-btn-secondary" onClick={() => navigate('/my')}>
                Мои заявки
              </button>
              <button type="button" className="native-btn native-btn-ghost" disabled={busy} onClick={() => void logout()}>
                Выйти
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="native-auth-center">
      <section className="native-auth-card" aria-labelledby="native-login-title">
        <div className="native-auth-card-top">
          <h1 id="native-login-title">Вход в личный кабинет</h1>
          <p>Подтверждение по коду из письма · без пароля</p>
        </div>
        <div className="native-auth-content">
          {info !== null ? (
            <p role="status" className="native-banner native-banner-success">
              {info}
            </p>
          ) : null}
          {error !== null ? (
            <p role="alert" className="native-banner native-banner-danger">
              {error}
            </p>
          ) : null}
          {phase === 'email' ? (
            <form onSubmit={(event) => void requestCode(event)}>
              <label htmlFor="native_email">
                Электронная почта
                <input
                  id="native_email"
                  className="native-input"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  disabled={busy}
                  onChange={(event) => {
                    setEmail(event.target.value);
                  }}
                />
              </label>
              <p className="native-field-note">На этот адрес будет отправлен одноразовый код.</p>
              <button type="submit" className="native-btn native-btn-primary" disabled={busy}>
                Получить код
              </button>
            </form>
          ) : (
            <form onSubmit={(event) => void verify(event)}>
              <p className="native-email-label">{email}</p>
              <label htmlFor="native_code">
                Код из письма
                <input
                  id="native_code"
                  className="native-input native-otp"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                  value={code}
                  disabled={busy}
                  onChange={(event) => {
                    setCode(event.target.value);
                  }}
                />
              </label>
              <div className="native-mini-actions">
                <button type="submit" className="native-btn native-btn-primary" disabled={busy}>
                  Войти
                </button>
                <button
                  type="button"
                  className="native-inline-btn"
                  disabled={busy}
                  onClick={() => {
                    setPhase('email');
                    setCode('');
                    setError(null);
                  }}
                >
                  Другой адрес
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
